import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  LOCALE_ID,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerProductService } from '../../../../services/product/customer.product.service';
import { CustomerProductVTO } from '../../../../pages/user-dashboard/user-product-details/user-product-details.model';
import { finalize, forkJoin } from 'rxjs';
import { ProductDetailsCard } from './product-details-card/product-details-card';
import { ProductInformationCard } from './product-information-card/product-information-card';
import { ImageCarousel } from '../../../admin-dashboard-component/product-details/utils/image-carousel/image-carousel';
import { Loading } from '../../../../utils/loading/loading';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { PharmacyCreditService } from '../../../../services/pharmacy-credit/pharmacy-credit';
import { RemainingQuantityIcon } from '../../../../shared/ui/icons/product-card/remaining-quantity-icon/remaining-quantity-icon';
import { LimitReachedIcon } from '../../../../shared/ui/icons/product-card/limit-reached-icon/limit-reached-icon';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-user-product-details-component',
  imports: [
    ProductDetailsCard,
    ProductInformationCard,
    ImageCarousel,
    Loading,
    ErrorData,
    RemainingQuantityIcon,
    LimitReachedIcon,
  ],
  templateUrl: './product-details-component.html',
  styleUrl: './product-details-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailsComponent implements OnInit {
  private customerProductService = inject(CustomerProductService);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private destroyRef = inject(DestroyRef);
  private storeId = computed(() => this.pharmacyCreditService.selectedPharmacy()?.id ?? null);
  private loadedStoreId: number | null | undefined;
  productId = input.required<string>();
  loadingProduct = signal(false);
  errorProduct = signal('');
  productData = signal<CustomerProductVTO | null>(null);
  productImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  productImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);

  isArabic = computed(() => this.locale.startsWith('ar'));

  // Product order limit for the selected pharmacy (null = no limit configured)
  remainingQuantity = computed(() => this.productData()?.remainingQuantity ?? null);
  isLimitReached = computed(() => {
    const remaining = this.remainingQuantity();

    return remaining != null && remaining <= 0;
  });

  constructor() {
    // The order limit badge depends on the Store-Id, and the header selects the pharmacy after the page loads
    effect(() => {
      const storeId = this.storeId();
      untracked(() => {
        if (this.loadedStoreId !== undefined && storeId !== this.loadedStoreId) this.loadCustomerProduct();
      });
    });
  }

  ngOnInit() {
    this.loadCustomerProduct();
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }

  loadCustomerProduct() {
    this.loadingProduct.set(true);
    this.errorProduct.set('');
    this.loadedStoreId = this.storeId();
    this.customerProductService
      .getCustomerProductById(this.productId(), this.loadedStoreId)
      .pipe(
        finalize(() => {
          this.loadingProduct.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.productData.set(res);
          this.productImageUrl.set({ url: '', isImage: false });

          this.fetchImage(res.profileImageFid)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (img) => this.productImageUrl.set(img),
              error: () => this.productImageUrl.set({ url: '', isImage: false }),
            });

          if (res.imageFids && res.imageFids.length > 1) {
            const imageRequests = res.imageFids.slice(1).map((fid) => this.fetchImage(fid));
            forkJoin(imageRequests)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (images) => this.productImages.set(images),
                error: () => this.productImages.set([]),
              });
          }
        },
        error: (err) => {
          this.errorProduct.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });
  }
}
